import { Link } from 'react-router-dom'
import { useT } from '../lib/i18n'
import { BRAND_KEY, IS_USTOOL } from '../lib/appKind'

/**
 * The application's name and mark.
 *
 * THE MARK. Two domes on a dashed road.
 *
 * It was a generic route glyph -- two waypoints and a line -- which said
 * "transport" and nothing about this business. The owner, 2026-10-09: *"keep
 * in mind it's for makkah and madinah make the icon accordingly"*. So: two
 * domes, deliberately unequal, standing on a road drawn as a dashed track,
 * because a qafila IS the caravan travelling between them. The whole product
 * in one glyph: two cities, and the journey joining them.
 *
 * Abstract on purpose. It does not depict the Kaaba or the Prophet's Mosque
 * -- a commercial brand should not borrow a sacred building's likeness, and
 * at 22px a literal one is a smudge anyway. A dome and a finial read as
 * "these cities" to everyone travelling there without claiming to be either
 * sanctuary, and without looking like the official Nusuk branding.
 *
 * Drawn to the app's own icon set: 24 viewBox, stroke, round caps, so it
 * sits beside the tab-bar icons rather than next to them.
 *
 * THE NAME. "For now", so the words live in the catalogue under `app.name`
 * and not in the markup -- renaming is one row, in seven languages, and
 * nothing else moves.
 */
export function Brand({ className = '', home = false }: {
  className?: string
  /**
   * Make the name a way back to the start, which is what tapping a logo
   * means everywhere else. It pushes a `home` stamp so Book now clears the
   * flow rather than merely re-selecting the route it is already on.
   *
   * Off by default: the sign-in screen renders this outside the app's
   * routes, where a Link has nothing to push onto.
   */
  home?: boolean
}) {
  const msg = useT()

  const inner = (
    <>
      <svg viewBox="0 0 24 24" className="h-[22px] w-[22px] shrink-0 text-accent"
           fill="none" stroke="currentColor" strokeWidth="1.9"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2.5 20.5h19" strokeDasharray="3.2 2.6" />
        <path d="M4.5 20.5v-4a3 3 0 0 1 6 0v4" />
        <path d="M13.5 20.5v-5a3.5 3.5 0 0 1 7 0v5" />
        <path d="M17 12v-2" />
      </svg>
      {/* The full stop frakt puts after its wordmark, in the accent -- the one
          piece of its identity worth keeping, because it turns a label into a
          name. It has to sit INSIDE the wordmark with no gap of its own; the
          row's gap-2 pushed it a word away and it read as a stray dot. */}
      <span className="flex items-baseline gap-0 font-display text-[18px] font-extrabold
                       tracking-tight">
        {/* Ustool wears its own name. Same mark -- the two domes and the
            caravan road are the group's identity, not one app's -- and the
            same full stop. Only the word changes (§71). */}
        <span className="text-ink">{msg(BRAND_KEY)}</span>
        <span className="text-accent" aria-hidden="true">.</span>
      </span>
    </>
  )

  const cls = `flex items-center gap-2 ${className}`

  if (!home) return <span className={cls}>{inner}</span>

  // Ustool has no Book now to go home to; its root is the requests inbox.
  return (
    <Link to={IS_USTOOL ? '/requests' : '/travel'} state={{ home: Date.now() }}
          className={cls} aria-label={msg(IS_USTOOL ? 'nav.requests' : 'nav.bookNow')}>
      {inner}
    </Link>
  )
}
